import { FormControl, Popper, TextField } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useEffect, useState } from "react";

import { palette } from "../../../theme/palette";

import { NONE } from "../../../consts/strings";
import {
  WorkScheduleDayAutocompleteMenuItem,
  WorkScheduleDayAutocompleteNoneMenuItem,
} from "./WorkScheduleDayAutocompleteMenuItem";

const useStyles = makeStyles({
  dropdownPopper: {
    zIndex: 1300,
    borderRadius: "0.5rem",
    boxShadow: `0px 3px 4px ${palette.typography.black.high}`,
  },
});

const getDisplayValue = (driver) =>
  !!driver?.driverCode
    ? `${driver.driverName} (${driver.driverCode.split("*")[1]})`
    : NONE;

export const WorkScheduleDayAutocomplete = ({
  className,
  label,
  name,
  error,
  helperText,
  drivers,
  onChange,
  selectedDriver,
  disabled,
  displayItemsCount = 10,
}) => {
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [highlightedItemIndex, setHighlightedItemIndex] = useState(-1);
  const [scrollOffsetIndex, setScrollOffsetIndex] = useState(0);
  const [touchState, setTouchState] = useState({});
  const [inputValue, setInputValue] = useState(null);
  const [isDropdownOpen, setIsDropDownOpen] = useState(false);

  useEffect(() => {
    if (!isDropdownOpen) return;
    setInputValue("");
    setHighlightedItemIndex(-1);
    setScrollOffsetIndex(0);
  }, [isDropdownOpen]);

  useEffect(() => {
    setHighlightedItemIndex(-1);
    setScrollOffsetIndex(0);
  }, [inputValue]);

  const showNoneItem = !inputValue || NONE.includes(inputValue.toLowerCase());
  const showNoneItemOffset = showNoneItem && !scrollOffsetIndex ? 1 : 0;
  const filteredDrivers =
    !isDropdownOpen || !inputValue
      ? drivers
      : drivers.filter((driver) =>
          getDisplayValue(driver)
            ?.toUpperCase()
            ?.includes(inputValue.toUpperCase())
        );

  const getDropdownItems = () =>
    showNoneItem && scrollOffsetIndex === 0
      ? [{ driverCode: null }, ...filteredDrivers.slice(0, 9)]
      : filteredDrivers.slice(
          scrollOffsetIndex,
          displayItemsCount + scrollOffsetIndex
        );

  const updatedToHighlightedDriver = () => {
    const { driverCode } =
      highlightedItemIndex === 0 && showNoneItem
        ? { driverCode: null }
        : filteredDrivers[highlightedItemIndex - showNoneItemOffset];
    (selectedDriver?.driverCode || null) !== driverCode &&
      onChange({ target: { name, value: driverCode } });
  };

  const displayValue = !isDropdownOpen
    ? getDisplayValue(selectedDriver)
    : inputValue;

  return (
    <FormControl
      className={className}
      variant={"outlined"}
      labelId={name}
      onKeyDown={({ code }) => {
        if (
          code === "ArrowDown" &&
          highlightedItemIndex < filteredDrivers.length + showNoneItemOffset
        ) {
          const newHighlightedItemIndex = highlightedItemIndex + 1;
          if (newHighlightedItemIndex >= scrollOffsetIndex + displayItemsCount)
            setScrollOffsetIndex(scrollOffsetIndex + 1);
          setHighlightedItemIndex(newHighlightedItemIndex);
        } else if (code === "ArrowUp" && highlightedItemIndex > 0) {
          const newHighlightedItemIndex = highlightedItemIndex - 1;
          if (newHighlightedItemIndex < scrollOffsetIndex)
            setScrollOffsetIndex(scrollOffsetIndex - 1);
          setHighlightedItemIndex(newHighlightedItemIndex);
        } else if (code === "Enter" && highlightedItemIndex > -1) {
          updatedToHighlightedDriver();
          setIsDropDownOpen(false);
        }
      }}
      onFocus={() => setIsDropDownOpen(true)}
      onClick={() => setIsDropDownOpen(true)}
      onBlur={() => {
        highlightedItemIndex > -1 && updatedToHighlightedDriver();
        setIsDropDownOpen(false);
      }}
    >
      <TextField
        ref={setAnchorEl}
        className={className}
        variant={"outlined"}
        label={label}
        labelId={name}
        name={name}
        value={displayValue}
        error={error}
        helperText={helperText}
        FormHelperTextProps={{
          style: { marginLeft: "0px", marginBottom: "-0.5rem" },
        }}
        onChange={({ target }) => setInputValue(target.value)}
        disabled={disabled}
      />
      <Popper
        open={isDropdownOpen}
        className={styles.dropdownPopper}
        anchorEl={anchorEl}
        // Background color has to be specified in style directly or it doesn't work
        style={{ backgroundColor: palette.white }}
        onMouseLeave={() => setHighlightedItemIndex(-1)}
        onWheel={({ deltaY }) => {
          const yPositive = deltaY > 0;
          if (
            !yPositive
              ? scrollOffsetIndex === 0
              : scrollOffsetIndex >= filteredDrivers.length - displayItemsCount
          )
            return;
          const newScrollOffsetIndex = scrollOffsetIndex + (yPositive ? 1 : -1);
          setScrollOffsetIndex(newScrollOffsetIndex);
        }}
        onTouchStart={({ touches: [{ clientY }] }) => {
          setTouchState({
            startY: clientY,
            startOffset: scrollOffsetIndex,
            lastTouchIndex: null,
          });
        }}
        onTouchMove={({ touches: [{ clientY }] }) => {
          const { startY, startOffset } = touchState;

          let indexDifference = 0;
          if (startY > clientY)
            indexDifference = Math.floor((startY - clientY) / 25);
          else if (startY < clientY)
            indexDifference = -Math.floor((clientY - startY) / 25);

          setTouchState({
            startY,
            startOffset: scrollOffsetIndex,
            lastTouchIndex: startOffset + indexDifference,
          });
        }}
        onTouchEnd={() => {
          const { lastTouchIndex } = touchState;

          let newScrollOffsetIndex = null;
          if (lastTouchIndex < 0) newScrollOffsetIndex = 0;
          else if (lastTouchIndex >= filteredDrivers.length - displayItemsCount)
            newScrollOffsetIndex = filteredDrivers.length - displayItemsCount;
          else newScrollOffsetIndex = lastTouchIndex;

          newScrollOffsetIndex !== null &&
            setScrollOffsetIndex(newScrollOffsetIndex);
          setTouchState({
            startY: null,
            startOffset: null,
            lastTouchIndex: null,
          });
        }}
      >
        {getDropdownItems().map((driver, index) => {
          const driverIndex = index + scrollOffsetIndex;
          const isHighlighted = driverIndex === highlightedItemIndex;
          const isSelected =
            driver.driverCode === (selectedDriver?.driverCode || null);
          const onMouseEnter = () => setHighlightedItemIndex(driverIndex);
          if (!driver.driverCode)
            return (
              <WorkScheduleDayAutocompleteNoneMenuItem
                isHighlighted={isHighlighted}
                isSelected={isSelected}
                onMouseEnter={onMouseEnter}
              />
            );
          else
            return (
              <WorkScheduleDayAutocompleteMenuItem
                isHighlighted={isHighlighted}
                isSelected={isSelected}
                onMouseEnter={onMouseEnter}
                {...driver}
              />
            );
        })}
      </Popper>
    </FormControl>
  );
};
